import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip,
    Grid, LinearProgress, MenuItem, Pagination, Paper, Stack, TextField,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';

import { exportClients, getClients } from '../../../actions/admin/clients';
import {
    CHANNEL_LABELS, SEGMENT_LABELS, SOURCE_LABELS, dateText,
    downloadClientExport, money
} from '../../../helpers/admin/clients/clientHelper';

const defaultFilters = {
    q: '', segment: '', purchaseStatus: '', channel: '', sourceSystem: '',
    branchId: '', state: '', minOrders: '', maxOrders: '', minSpend: '', maxSpend: '',
    firstOrderFrom: '', firstOrderTo: '', lastOrderFrom: '', lastOrderTo: '',
    productId: '', variantId: '', tag: '', sortBy: 'spend', sortDir: 'desc', page: 1, limit: 25
};

const CustomerDirectory = ({ branches }) => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialFilters = () => ({ ...defaultFilters, ...Object.fromEntries(searchParams.entries()), page: Number(searchParams.get('page')) || 1 });
    const [draft, setDraft] = useState(initialFilters);
    const [filters, setFilters] = useState(initialFilters);
    const [data, setData] = useState({ items: [], pagination: { page: 1, pages: 1, total: 0 } });
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        getClients(filters).then(result => {
            if (!active) return;
            if (result.status) setData({ items: result.items, pagination: result.pagination });
            setLoading(false);
        });
        return () => { active = false; };
    }, [filters]);

    const setField = event => setDraft(current => ({ ...current, [event.target.name]: event.target.value }));
    const commitFilters = next => {
        setFilters(next);
        const params = {};
        Object.entries(next).forEach(([key, value]) => {
            if (value !== '' && value !== undefined && value !== null && value !== defaultFilters[key]) params[key] = value;
        });
        setSearchParams(params, { replace: true });
    };
    const apply = () => commitFilters({ ...draft, page: 1 });
    const reset = () => {
        setDraft(defaultFilters);
        commitFilters(defaultFilters);
    };
    const download = async () => {
        setExporting(true);
        const result = await exportClients(filters);
        if (result.status) downloadClientExport(result.rows);
        setExporting(false);
    };

    const columns = useMemo(() => [
        ['Cliente', customer => (
            <Box>
                <Typography fontWeight={600}>{customer.contact?.display_name || 'Sin nombre'}</Typography>
                <Typography variant="caption" color="text.secondary">{customer.contact?.primary_email || 'Sin correo'}</Typography>
            </Box>
        )],
        ['Segmento', customer => <Chip size="small" label={SEGMENT_LABELS[customer.metrics?.rfm?.segment] || 'Sin clasificar'} />],
        ['Pedidos', customer => customer.metrics?.orders_count || 0],
        ['Venta aprobada', customer => money(customer.metrics?.gross_spend)],
        ['Ticket promedio', customer => money(customer.metrics?.average_order_value)],
        ['Última compra', customer => dateText(customer.metrics?.last_order_at)],
        ['Canal preferido', customer => CHANNEL_LABELS[customer.metrics?.favorite_channel] || '—']
    ], []);

    return (
        <Box>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TextField
                        fullWidth size="small" name="q" value={draft.q} onChange={setField}
                        label="Buscar por nombre, correo o teléfono"
                        onKeyDown={event => event.key === 'Enter' && apply()}
                    />
                    <Button variant="contained" startIcon={<SearchIcon />} onClick={apply}>Buscar</Button>
                    <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={download} disabled={exporting}>
                        Exportar
                    </Button>
                </Stack>
                <Accordion elevation={0} disableGutters sx={{ mt: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>Filtros avanzados</Typography></AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}><TextField select fullWidth size="small" label="Segmento" name="segment" value={draft.segment} onChange={setField}>
                                <MenuItem value="">Todos</MenuItem>{Object.entries(SEGMENT_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                            </TextField></Grid>
                            <Grid item xs={12} sm={6} md={3}><TextField select fullWidth size="small" label="Tipo de comprador" name="purchaseStatus" value={draft.purchaseStatus} onChange={setField}>
                                <MenuItem value="">Todos</MenuItem><MenuItem value="never">Sin compra</MenuItem><MenuItem value="one_time">Una compra</MenuItem><MenuItem value="returning">Recurrente</MenuItem>
                            </TextField></Grid>
                            <Grid item xs={12} sm={6} md={3}><TextField select fullWidth size="small" label="Canal" name="channel" value={draft.channel} onChange={setField}>
                                <MenuItem value="">Todos</MenuItem>{Object.entries(CHANNEL_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                            </TextField></Grid>
                            <Grid item xs={12} sm={6} md={3}><TextField select fullWidth size="small" label="Sistema fuente" name="sourceSystem" value={draft.sourceSystem} onChange={setField}>
                                <MenuItem value="">Todos</MenuItem>{Object.entries(SOURCE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                            </TextField></Grid>
                            <Grid item xs={12} sm={6} md={3}><TextField select fullWidth size="small" label="Sucursal favorita" name="branchId" value={draft.branchId} onChange={setField}>
                                <MenuItem value="">Todas</MenuItem>{branches.map(branch => <MenuItem key={branch._id} value={branch._id}>{branch.title}</MenuItem>)}
                            </TextField></Grid>
                            {['state', 'tag', 'productId', 'variantId'].map(name => <Grid item xs={12} sm={6} md={3} key={name}><TextField fullWidth size="small" name={name} value={draft[name]} onChange={setField} label={{ state: 'Estado', tag: 'Etiqueta', productId: 'ID de producto', variantId: 'ID de variante' }[name]} /></Grid>)}
                            {['minOrders', 'maxOrders', 'minSpend', 'maxSpend'].map(name => <Grid item xs={6} md={3} key={name}><TextField fullWidth size="small" type="number" name={name} value={draft[name]} onChange={setField} label={{ minOrders: 'Pedidos mínimos', maxOrders: 'Pedidos máximos', minSpend: 'Compra mínima', maxSpend: 'Compra máxima' }[name]} /></Grid>)}
                            {['firstOrderFrom', 'firstOrderTo', 'lastOrderFrom', 'lastOrderTo'].map(name => <Grid item xs={12} sm={6} md={3} key={name}><TextField fullWidth size="small" type="date" name={name} value={draft[name]} onChange={setField} InputLabelProps={{ shrink: true }} label={{ firstOrderFrom: 'Primera compra desde', firstOrderTo: 'Primera compra hasta', lastOrderFrom: 'Última compra desde', lastOrderTo: 'Última compra hasta' }[name]} /></Grid>)}
                            <Grid item xs={12} sm={6} md={3}><TextField select fullWidth size="small" label="Ordenar por" name="sortBy" value={draft.sortBy} onChange={setField}>
                                <MenuItem value="spend">Venta aprobada</MenuItem><MenuItem value="orders">Pedidos</MenuItem><MenuItem value="lastOrder">Última compra</MenuItem><MenuItem value="name">Nombre</MenuItem><MenuItem value="createdAt">Registro</MenuItem>
                            </TextField></Grid>
                            <Grid item xs={12} sm={6} md={3}><TextField select fullWidth size="small" label="Dirección" name="sortDir" value={draft.sortDir} onChange={setField}>
                                <MenuItem value="desc">Descendente</MenuItem><MenuItem value="asc">Ascendente</MenuItem>
                            </TextField></Grid>
                        </Grid>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                            <Button onClick={reset}>Limpiar</Button><Button variant="contained" onClick={apply}>Aplicar filtros</Button>
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            </Paper>
            {loading && <LinearProgress />}
            <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 1050 }}>
                    <Box component="thead" sx={{ bgcolor: '#f5f7f8' }}><Box component="tr">
                        {columns.map(([label]) => <Box component="th" key={label} sx={{ p: 1.5, textAlign: 'left', fontSize: 13 }}>{label}</Box>)}
                    </Box></Box>
                    <Box component="tbody">
                        {!loading && data.items.map(customer => <Box component="tr" key={customer._id} onClick={() => navigate(`/nefro-admin/clientes/${customer._id}`, { state: { returnSearch: searchParams.toString() } })} sx={{ cursor: 'pointer', borderTop: '1px solid #eee', '&:hover': { bgcolor: '#f7fbfd' } }}>
                            {columns.map(([label, render]) => <Box component="td" key={label} sx={{ p: 1.5, fontSize: 14 }}>{render(customer)}</Box>)}
                        </Box>)}
                    </Box>
                </Box>
                {!loading && !data.items.length && <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>No se encontraron clientes.</Typography>}
            </Paper>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">{data.pagination.total || 0} clientes</Typography>
                <Pagination count={Math.max(1, data.pagination.pages || 1)} page={filters.page} onChange={(_, page) => {
                    const next = { ...filters, page };
                    setDraft(current => ({ ...current, page })); commitFilters(next);
                }} />
            </Stack>
        </Box>
    );
};

export default CustomerDirectory;
